import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, type FormProps } from '~/components/Form';
import { FormFieldInput, FormFieldTextArea } from '~/components/FormField';
import { getI18nErrorMap } from '~/lib/getI18nErrorMap';
import { SelectLabels } from '~/modules/settings/components/SelectLabels';

import type { RequestSublabelFormSchema } from './schema';
import { requestSublabelFormSchema } from './schema';

type LabelFormProps = Omit<
  FormProps<RequestSublabelFormSchema>,
  'formHook' | 'schema'
>;

export function RequestSublabelForm({
  values,
  onSubmit,
  ...formProps
}: LabelFormProps) {
  const { t } = useTranslation('settings');

  const form = useForm<RequestSublabelFormSchema>({
    values,
    defaultValues: {
      name: '',
      notes: '',
    },
    resolver: zodResolver(requestSublabelFormSchema, {
      error: getI18nErrorMap((s) => t(`sublabelRequest.validations.${s}`)),
    }),
  });

  return (
    <Form
      data-testid="sublabel-request-form"
      formHook={form}
      onSubmit={onSubmit}
      schema={requestSublabelFormSchema}
      {...formProps}
    >
      <div className="rounded-sm p-lg bg-level-02">
        <div className="flex w-full max-w-[52rem] gap-md">
          <FormFieldInput
            name="name"
            className="min-w-xs flex-1"
            label={t('label.labelName')}
            placeholder={t('label.placeholder.labelName')}
          />
          <SelectLabels
            className="min-w-xs flex-1"
            name="parentLabel"
            multiselect={false}
            labelType="parent_label"
            label={t('label.parentLabel')}
            placeholder={t('sublabelRequest.placeholders.parentLabel')}
          />
        </div>

        <FormFieldTextArea
          name="notes"
          label={t('notes')}
          placeholder={t('sublabelRequest.placeholders.notes')}
          className="mt-md w-full max-w-[52rem]"
        />
      </div>
    </Form>
  );
}

RequestSublabelForm.displayName = 'RequestSublabelForm';
